import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { switchMap, map, pipe, take, filter, debounceTime } from 'rxjs';
import {
  SetSpecialtyColdChainInvoice,
  SetSpecialtyColdChainInvoiceNeedHelp,
  SetSpecialtyColdChainCommodity,
  SetSColdChainIsDomestic,
  SetSpecialtyColdChainInsuranceMaxValueAndAutoDescription,
} from './specialty-cold-chain.actions';
import { IShipmentCommodityModel } from 'src/app/modules/customer/features/commodity/models/commodity.model';
import {
  selectColdChainShipment,
  selectColdChainInternational,
} from './specialty-cold-chain.selector';

@Injectable()
export class SpecialtyColdChainEffects {
  actions$ = inject(Actions);
  store = inject(Store);

  computeInsuranceMaxValueAndAutoDescription = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SetSpecialtyColdChainInvoice,
        SetSpecialtyColdChainInvoiceNeedHelp,
        SetSpecialtyColdChainCommodity,
        SetSColdChainIsDomestic
      ),
      switchMap(() => {
        return this.store.pipe(select(selectColdChainShipment), take(1));
      }),
      debounceTime(100),
      filter((shipment) => shipment.isDomestic === false),
      switchMap(() => {
        return this.store.pipe(select(selectColdChainInternational), take(1));
      }),
      map((international) => {
        let insuranceMaxValue = null;
        let insuranceAutoDescription = null;

        const isNeedHelp = international.commercialInvoice.isNeedHelp;
        const isUploadDocument =
          international.commercialInvoice.isUploadDocument;

        if (!isNeedHelp && isUploadDocument) {
          insuranceMaxValue =
            international.commercialInvoice.value?.totalCustomsValue || null;
        } else {
          const commodityList = international.commodities.value || [];

          insuranceMaxValue =
            commodityList
              .map(
                (item: IShipmentCommodityModel) => Number(item?.totalValue) || 0
              )
              .reduce((accumulator: number, currentValue: number) => {
                return accumulator + currentValue;
              }, 0) || null;

          insuranceAutoDescription =
            commodityList
              .map((item: IShipmentCommodityModel) => item?.description?.trim())
              .filter(
                (description: string | undefined): description is string =>
                  !!description && description.length > 0
              )
              .map(
                (description: string) =>
                  description.charAt(0).toUpperCase() + description.slice(1)
              )
              .join('. ') || null;
        }

        return SetSpecialtyColdChainInsuranceMaxValueAndAutoDescription({
          insuranceMaxValue,
          insuranceAutoDescription,
        });
      })
    )
  );
}
